import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { filesApi } from '../../../api/files.api';
import type { FileFilters } from '../types/file.types';
import { QUERY_STALE_TIME, QUERY_CACHE_TIME } from '@/constants/queryConstants';
import { useAppSelector } from '@/store/hooks';
import { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { FILES_QUERY_KEYS } from '../constants/queryKeys';

export const selectFilesParams = createSelector(
  (state: RootState) => state.files.filters,
  (state: RootState) => state.files.currentPage,
  (state: RootState) => state.files.pageSize,
  (state: RootState) => state.files.sorting,
  (state: RootState) => state.files.searchValue,
  (state: RootState) => state.files.onlyMyDocs,
  (filters: FileFilters[], page, pageSize, sorting, search, onlyMe) => ({
    filters,
    page,
    pageSize,
    sortBy: sorting[0]?.id || 'order_nb',
    sortOrder:
      sorting[0] && !sorting[0]?.desc ? ('asc' as const) : ('desc' as const),
    search,
    onlyMe
  })
);

export const useFilesQuery = () => {
  const params = useAppSelector(selectFilesParams);

  const queryKey = useMemo(() => FILES_QUERY_KEYS.LIST(params), [params]);
  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await filesApi.getList(params);

      if (response.documents.length === 0) {
        return {
          documents: [],
          total_count: 0,
        };
      }

      return response;
    },
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_CACHE_TIME,
    placeholderData: keepPreviousData,
  });
};
